import { motion } from 'motion/react';
import type { QuestionType } from '@/types';
import styles from './QuestionTypeSelector.module.scss';

interface QuestionTypeSelectorProps {
  value: QuestionType;
  onChange: (type: QuestionType) => void;
  showMath?: boolean;
}

interface QuestionTypeOption {
  value: QuestionType;
  label: string;
  description: string;
}

const questionTypes: QuestionTypeOption[] = [
  {
    value: 'multiple_choice',
    label: 'Multiple Choice',
    description: 'Select one or more correct answers from a list of options',
  },
  {
    value: 'fill_blank',
    label: 'Fill in the Blank',
    description: 'Complete the sentence by filling in missing words',
  },
  {
    value: 'mix_match',
    label: 'Mix and Match',
    description: 'Match items from two columns by pairing them correctly',
  },
];

const mathOption: QuestionTypeOption = {
  value: 'math',
  label: 'Math Problem',
  description: 'Solve mathematical problems with LaTeX support',
};

export function QuestionTypeSelector({ value, onChange, showMath = false }: QuestionTypeSelectorProps) {
  const availableTypes = showMath ? [...questionTypes, mathOption] : questionTypes;

  return (
    <div className={styles.typeSelector}>
      <label className={styles.typeSelector__label}>
        Question Type <span className={styles.typeSelector__required}>*</span>
      </label>

      <select
        className={styles.typeSelector__select}
        value={value}
        onChange={(e) => onChange(e.target.value as QuestionType)}
      >
        {availableTypes.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>

      <motion.p
        className={styles.typeSelector__description}
        key={value}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {availableTypes.find((t) => t.value === value)?.description}
      </motion.p>
    </div>
  );
}
